import { Avatar } from "@radix-ui/themes";
import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { publicConfig, uploadUrl } from "@/config/publicConfig";
import { timezones } from "../../../enums/timezones";
import { useFetch } from "../../../hooks/useFetch";
import { useFetchLicense } from "../../../hooks/useFetchLicense";
import { useAppStore } from "../../../stores/RootStore";
import { Site, Upload } from "../../../types/types";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input/Input";
import { Label } from "../../../ui/Label/Label";
import { ScrollArea } from "../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../ui/SelectButton/SelectButton";
import { Separator } from "../../../ui/Separator";

const OrganizationSettings = () => {
  const currentSite = useAppStore((store) => store.currentSite);
  const setCurrentSite = useAppStore((store) => store.setCurrentSite);
  const timezonesList = timezones.map((timezone) => {
    return { value: timezone, label: timezone };
  });

  const [imageId, setImageId] = useState(currentSite?.image.id);

  const [errors, setErrors] = useState<Array<any>>([]);
  const [invalidLicense, setInvalidLicense] = useState(false);
  const { register, reset, handleSubmit, control, getValues } = useForm({
    defaultValues: {
      name: currentSite?.name,
      subdomain: currentSite?.subdomain,
      timezone: { value: currentSite?.timezone, label: currentSite?.timezone },
      license: currentSite?.license_key,
    },
  });

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/users/sites/${currentSite?.id}`,
    true
  );

  // Проверка существования ключа организации
  const [{ response: licenseResponse, error: licenseError, statusCode }, doFetchLicenseCheck] =
    useFetchLicense(`/licenses/check`);

  // Успешная проверка лицензии
  useEffect(() => {
    if (!licenseResponse) return;
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          name: getValues("name"),
          timezone: getValues("timezone.value"),
          license_key: getValues("license"),
        },
      },
    });
  }, [licenseResponse]);

  // Провальная проверка лицензии
  useEffect(() => {
    if (!licenseError) return;
    if (statusCode == "404") {
      setInvalidLicense(true);
      console.log("Отсутствует такой ключ организации");
      reset({
        name: currentSite?.name,
        subdomain: currentSite?.subdomain,
        timezone: { value: currentSite?.timezone, label: currentSite?.timezone },
        license: currentSite?.license_key,
      });
    }
    if (statusCode == "403") {
      console.log("Срок лицензии истёк или превышено количество активированных копий");
    }
  }, [licenseError]);

  // Загрузка изображения
  const [
    { response: imageUploadResponse, isLoading: imageUploadIsLoading, error: imageUploadError },
    doFetchImageUpload,
  ] = useFetch(`/uploads`, true, true);

  const onSubmit = (formData: any) => {
    setErrors([]);
    setInvalidLicense(false);
    // Проверка ключа организации на корректность при изменении ключа
    if (publicConfig.licensingEnabled && formData.license != currentSite?.license_key)
      doFetchLicenseCheck({
        method: "POST",
        data: {
          key: formData.license,
          workplace_key: "test1", // В web версии нельзя получить machine key
        },
      });
    // Отсутствие изменения ключа организации
    else
      doFetchItemSave({
        method: "PUT",
        data: {
          data: {
            name: formData.name,
            timezone: formData.timezone.value,
          },
        },
      });
  };

  const onFileChoice = (value: string | Blob | File) => {
    const formData = new FormData();
    formData.set("attachment", value);
    doFetchImageUpload({
      method: "POST",
      data: formData,
    });
  };

  useEffect(() => {
    if (!imageUploadResponse) {
      return;
    }
    const imageUploadData = imageUploadResponse.data as unknown as Upload;
    setImageId(imageUploadData.id);
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          image: { ...imageUploadResponse.data },
        },
      },
    });
  }, [imageUploadResponse]);

  const addSite = (itemSaveResponse: { data: { attributes: any } }) => {
    const newSiteData = { ...currentSite, ...itemSaveResponse.data.attributes } as unknown as Site;
    setCurrentSite(newSiteData);
  };

  useEffect(() => {
    if (!saveResponse) return;
    addSite(saveResponse);
  }, [saveResponse]);

  useEffect(() => {
    if (!saveError) return;
    setErrors(saveError.errors);
  }, [saveError]);

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background px-6">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('company_settings')}</span>
        </div>
      </div>
      <ScrollArea>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6">
          <div className="grid grid-cols-3">
            <div className="col-span-1 pt-4">
              <label className="flex flex-col items-center">
                <Avatar
                  size="7"
                  src={imageId ? uploadUrl(imageId) : ""}
                  fallback={
                    currentSite?.name
                      .split(" ")
                      .map((word) => word[0])
                      .join("")!
                  }
                />
                <span className="mt-2 text-base leading-normal">{t('select_file')}</span>
                {/*@ts-ignore */}
                <input className="hidden" type="file" onChange={(evt) => onFileChoice(evt.target.files[0])} />
              </label>
            </div>
            <div className="col-span-2 flex flex-col w-full h-full gap-2">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("title_name")}*`} />
                  </div>
                )}
              />
              <Controller
                name="subdomain"
                control={control}
                disabled
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("subdomain")}`} />
                  </div>
                )}
              />

            </div>
          </div>
          <Separator className="my-4"/>
          <div className="flex flex-col w-full h-full gap-2"> 
            <Controller
              name="timezone"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("timezone")}*</Label>
                  <SelectButton
                    label={"Выберите временную зону"}
                    searchable={true}
                    options={timezonesList}
                    value={value ? timezonesList.find((s) => s.value == value.value)! : null}
                    onChange={onChange}
                  />
                </div>
              )}
            />
            {publicConfig.licensingEnabled && invalidLicense && (
              <li className="text-red-700 w-full">{t("error")}: введённый ключ организации неверен</li>
            )}
            {publicConfig.licensingEnabled && <Controller
              name="license"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={`${t("license")}*`} />
                </div>
              )}
            />}
          </div>
            <Button type="submit" className="mt-8">
              {t("save")}
            </Button>
        </form>
      </ScrollArea>
    </div>
  );
};

export default OrganizationSettings;
